import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { MaskLines } from "./Reveal";
import { ConstellationSky } from "./ConstellationSky";

const tags = ["Curiosity", "Science", "Skepticism", "Wonder"];

const TiltLogo = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 14 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const leave = () => { mx.set(0); my.set(0); };
  return (
    <div style={{ perspective: 900 }} onMouseMove={move} onMouseLeave={leave}>
      <motion.div
        data-testid="hero-logo"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 0.85, rotate: -12 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1.4, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-40 sm:w-52 lg:w-72"
      >
        <div className="absolute inset-[22%] rounded-full bg-lime/10 blur-3xl" aria-hidden="true" />
        <img
          src="/img/logo-flower.png"
          alt="The Desi Science Page logo"
          className="relative w-full h-auto animate-float drop-shadow-[0_12px_24px_rgba(0,0,0,0.55)]"
          style={{ transform: "translateZ(40px)" }}
        />
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden flex items-end">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src="/img/hero.jpg" alt="" className="w-full h-full object-cover object-[60%_center] grayscale-[35%] brightness-[0.45]" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
      <div className="absolute inset-0 grid-lines opacity-40" />
      <div className="spotlight absolute inset-0" />
      <ConstellationSky containerRef={ref} />

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full px-5 sm:px-10 lg:px-16 pb-14 pt-32">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} data-testid="hero-tagline" className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-8">
              <span className="h-px w-10 bg-lime" aria-hidden="true" />
              {tags.map((t, i) => (
                <span key={t} className="flex items-center gap-4 font-mono text-[12px] sm:text-[13px] uppercase tracking-[0.3em] text-zinc-200">
                  {t}
                  {i < tags.length - 1 && <span className="w-1 h-1 rounded-full bg-lime" aria-hidden="true" />}
                </span>
              ))}
            </motion.div>
            <h1 data-testid="hero-title" className="font-display font-bold uppercase leading-[0.85] tracking-tight text-[15vw] sm:text-7xl lg:text-[8.5rem] xl:text-[10rem]">
              <MaskLines onLoad delay={0.3} lines={["The Desi", <span className="text-lime">Science</span>, <span className="outline-text">Page</span>]} />
            </h1>
          </div>
          <TiltLogo />
        </div>
        <motion.p
          data-testid="hero-intro-quote"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-10 max-w-2xl font-serif italic text-xl sm:text-2xl lg:text-3xl text-zinc-100 leading-snug border-l-2 border-lime pl-5"
        >
          This is a place for those who ask questions, doubt easy answers, and enjoy the slow, fascinating process of figuring things out.!
        </motion.p>
      </motion.div>
    </section>
  );
};
